import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { getAuthToken, setAuthTokenWithReadback, clearAuthToken, clearFlowId, ApiError } from '@core/api';
import { captureAuthError } from '@core/sentry/utils';
import { safeLocalStorage } from '@shared/utils/safeStorage';
import type { AuthState, AuthContextValue } from './types';
import { redirectToAuth } from './authRedirect';
import { getSubdomainPrefix } from '@core/tenant/getSubdomainPrefix';
import { reportStorageAvailability } from '@shared/utils/storageAvailability';
import {
  parseAuthReturn,
  cleanAuthReturnUrl,
  saveAuthReturnFallback,
  loadAuthReturnFallback,
  clearAuthReturnFallback,
  isAuthCallback,
} from './authReturn';
import { getUserInfo } from './authApi';

/**
 * Storage keys for auth persistence
 */
const AUTH_USER_KEY = 'dineflow_auth_user';

/**
 * User data stored in localStorage
 */
interface StoredUser {
  userId: string;
  email: string;
  name?: string;
  phone?: string;
  preferred?: boolean;
  blockedInRestaurantsId?: string[];
  roles?: string[];
  userRestaurantId?: string | number | number[] | null;
  /** @deprecated kept optional only for retrocompat with prior localStorage entries */
  expiresAt?: number;
}

/**
 * The route a path is on, for telemetry: its first segment, and `/*` when more follow (`/reset/<token>` →
 * `/reset/*`). The later segments are dropped: on a route with parameters they hold them, tokens among them.
 */
function routeLabel(pathname: string): string {
  const [first = '', ...rest] = pathname.split('/').filter(Boolean);
  return rest.length > 0 ? `/${first}/*` : `/${first}`;
}

/**
 * Default auth state
 */
const defaultAuthState: AuthState = {
  isAuthenticated: false,
};

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider component that manages user authentication state
 * with persistence across page refreshes
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Load stored user data from localStorage
   */
  const loadStoredUser = useCallback((): StoredUser | null => {
    const stored = safeLocalStorage.getItem(AUTH_USER_KEY);
    if (!stored) return null;

    try {
      return JSON.parse(stored) as StoredUser;
    } catch {
      return null;
    }
  }, []);

  /**
   * Save user data to localStorage
   */
  const saveStoredUser = useCallback((user: StoredUser): void => {
    safeLocalStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }, []);

  /**
   * Clear stored user data
   */
  const clearStoredUser = useCallback((): void => {
    safeLocalStorage.removeItem(AUTH_USER_KEY);
  }, []);

  /**
   * Fetch user profile from API and update auth state + storage.
   * Called after login and on init when we have a token but no name yet.
   */
  const fetchAndStoreProfile = useCallback(async (subdomainPrefix?: string) => {
    try {
      const data = await getUserInfo(subdomainPrefix);
      const profile = data.user;
      // Trim whitespace defensively: backend has historically returned " " for
      // unset name/phone, which would falsely satisfy the profile completion
      // gate. Empty strings are stored so UI fallbacks treat them as missing.
      const name = (profile.full_name || '').trim();
      const email = (profile.user?.email || '').trim();
      const phone = (profile.phone || '').trim();
      const userId = profile.user?.id ? String(profile.user.id) : '';
      const preferred = !!(data as unknown as Record<string, unknown>).preferred;
      const blockedInRestaurantsId = data.blockedInRestaurantsId ?? [];
      const roles = profile.user?.roles ?? [];
      const userRestaurantId = data.userRestaurantId ?? null;

      const token = getAuthToken();
      if (token) {
        saveStoredUser({ userId, email, name, phone, preferred, blockedInRestaurantsId, roles, userRestaurantId });
      }

      setAuthState((prev) => ({
        ...prev,
        userId,
        email: email || prev.email,
        name,
        phone,
        preferred,
        blockedInRestaurantsId,
        roles,
        userRestaurantId,
      }));
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined;
      const tokenStillExists = !!getAuthToken();
      Sentry.addBreadcrumb({
        category: 'auth-return',
        message: 'fetchAndStoreProfile failed',
        level: 'warning',
        data: {
          status,
          errorMessage: err instanceof Error ? err.message : String(err),
          tokenStillInStorage: tokenStillExists,
        },
      });
      if (status !== 401) throw err;
    }
  }, [saveStoredUser]);

  /**
   * Login with a token directly (e.g. from passwordless flow).
   * Fetches user profile BEFORE setting isAuthenticated so that phone/name
   * are available when the flow orchestrator re-evaluates active steps.
   */
  const loginWithToken = useCallback(async (token: string, email: string, flowId?: string | null) => {
    const tokenWriteReadback = setAuthTokenWithReadback(token, { flowId });
    const tokenPresentAfterWrite = !!getAuthToken();

    Sentry.addBreadcrumb({
      category: 'auth-return-write-readback',
      message: 'Token write/readback check',
      level: tokenWriteReadback ? 'info' : 'warning',
      data: {
        flowId: flowId ?? null,
        tokenWriteReadback,
        tokenPresentAfterWrite,
      },
    });

    // Storage write failed. Do NOT proceed — fetchAndStoreProfile would run
    // under a stale token (or no token), yielding wrong-account hydration.
    // captureAuthError already recorded the failure inside
    // setAuthTokenWithReadback.
    if (!tokenWriteReadback) {
      clearAuthToken();
      clearStoredUser();
      setAuthState(defaultAuthState);
      throw new Error('auth.token_write_readback_mismatch');
    }

    const user: StoredUser = {
      userId: '',
      email,
    };
    saveStoredUser(user);

    const prefix = getSubdomainPrefix();
    let restaurantPrefix = prefix ?? undefined;
    if (prefix === 'auth') {
      captureAuthError('auth.login_on_auth_subdomain', {
        flowId,
        route: routeLabel(window.location.pathname),
      });
      restaurantPrefix = undefined;
    }

    try {
      await fetchAndStoreProfile(restaurantPrefix);
    } catch (fetchErr) {
      captureAuthError('auth.login_failed_post_fetch', {
        flowId,
        tokenWriteReadback,
        fetchErrorStatus: fetchErr instanceof ApiError ? fetchErr.status : undefined,
        fetchErrorMessage: fetchErr instanceof Error ? fetchErr.message : String(fetchErr),
      });
      clearAuthToken();
      clearStoredUser();
      setAuthState(defaultAuthState);
      throw new Error('auth.login_failed_post_fetch');
    }

    if (!getAuthToken()) {
      captureAuthError('auth.login_failed_post_fetch', {
        flowId,
        tokenWriteReadback,
        reason: 'token_cleared_post_fetch',
      });
      clearStoredUser();
      setAuthState(defaultAuthState);
      throw new Error('auth.login_failed_post_fetch');
    }

    setAuthState((prev) => ({
      ...prev,
      isAuthenticated: true,
      email: prev.email || email,
      token,
    }));
  }, [saveStoredUser, fetchAndStoreProfile, clearStoredUser]);

  /**
   * Update local profile data (name, phone) after the user saves via API.
   * Updates both authState and localStorage so FlowContext re-evaluates.
   */
  const updateProfile = useCallback((updates: { name?: string; phone?: string }) => {
    setAuthState((prev) => ({
      ...prev,
      ...updates,
    }));

    // Also update localStorage so data survives refresh
    const stored = loadStoredUser();
    if (stored) {
      saveStoredUser({
        ...stored,
        ...(updates.name !== undefined ? { name: updates.name } : {}),
        ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
      });
    }
  }, [loadStoredUser, saveStoredUser]);

  /**
   * Login - redirect to auth subdomain
   */
  const login = useCallback(() => {
    redirectToAuth('login');
  }, []);

  /**
   * Signup - redirect to auth subdomain
   */
  const signup = useCallback(() => {
    redirectToAuth('signup');
  }, []);

  /**
   * Logout - clear auth state and tokens
   */
  const logout = useCallback(() => {
    clearAuthToken();
    clearFlowId();
    clearStoredUser();
    setAuthState(defaultAuthState);
  }, [clearStoredUser]);

  /**
   * Check if auth is still valid
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    const storedUser = loadStoredUser();

    if (!token || !storedUser) {
      clearFlowId();
      setAuthState(defaultAuthState);
      return false;
    }

    return true;
  }, [loadStoredUser]);

  /**
   * Initialize auth state from storage or callback
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      reportStorageAvailability();

      Sentry.addBreadcrumb({
        category: 'auth-init',
        message: 'initAuth started',
        level: 'info',
        data: {
          route: routeLabel(window.location.pathname),
          isAuthCallback: isAuthCallback(),
          hasTokenInStorage: !!getAuthToken(),
          hasSearchParams: window.location.search.length > 1,
        },
      });

      try {
        // Check if returning from auth callback.
        // Note: /auth-return page handles the new cross-subdomain flow via
        // loginWithToken(). This legacy path handles ?authToken= on any other route.
        if (isAuthCallback() && !window.location.pathname.includes('/auth-return')) {
          const fromUrl = parseAuthReturn();
          const hasUrlToken = !!fromUrl.authState?.token;
          const fromFallback = hasUrlToken ? null : loadAuthReturnFallback();
          const payload = hasUrlToken ? fromUrl : (fromFallback ?? fromUrl);

          if (payload.authState?.isAuthenticated && payload.authState.token) {
            saveAuthReturnFallback(payload);
            cleanAuthReturnUrl();
            await loginWithToken(payload.authState.token, payload.authState.email || '', payload.flowId);
            if (getAuthToken()) {
              clearAuthReturnFallback();
            }
          } else {
            cleanAuthReturnUrl();
          }
        } else if (!window.location.pathname.includes('/auth-return')) {
          // Check for existing token and user
          const token = getAuthToken();
          const storedUser = loadStoredUser();

          if (token && storedUser) {
            setAuthState({
              isAuthenticated: true,
              userId: storedUser.userId,
              email: storedUser.email,
              name: storedUser.name?.trim(),
              phone: storedUser.phone?.trim(),
              preferred: storedUser.preferred,
              blockedInRestaurantsId: storedUser.blockedInRestaurantsId,
              roles: storedUser.roles,
              userRestaurantId: storedUser.userRestaurantId,
              token,
            });
          } else {
            clearFlowId();
          }
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [loadStoredUser, saveStoredUser, fetchAndStoreProfile]);

  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, [logout]);

  const contextValue: AuthContextValue = {
    authState,
    isLoading,
    login,
    signup,
    logout,
    checkAuth,
    loginWithToken,
    updateProfile,
    refreshProfile: fetchAndStoreProfile,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook to get just the auth state
 */
export function useAuthState(): AuthState {
  const { authState } = useAuth();
  return authState;
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { authState } = useAuth();
  return authState.isAuthenticated;
}

export default AuthProvider;
